import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { Button } from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import { Helmet } from 'react-helmet-async';


type LeadRow = {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  message: string | null;
  status: string;
  source_page: string | null;
  created_at: string;
};

export default function AdminLeads() {
  const [leads, setLeads] = useState<LeadRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // modal state
  const [selectedLead, setSelectedLead] = useState<LeadRow | null>(null);
  const [savingLead, setSavingLead] = useState(false);

  useEffect(() => {
    loadLeads();
  }, []);

  const loadLeads = async () => {
    setLoading(true);
    setError(null);
    const { data, error } = await supabase
      .from("leads")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Load leads error:", error);
      setError(error.message);
    } else {
      setLeads((data || []) as LeadRow[]);
    }
    setLoading(false);
  };

  const updateStatus = async (id: string, status: string) => {
    setUpdating(id);
    const { error } = await supabase
      .from("leads")
      .update({ status })
      .eq("id", id);

    if (error) {
      console.error("Status update error:", error);
      alert("Error updating status: " + error.message);
    } else {
      setLeads((prev) =>
        prev.map((lead) =>
          lead.id === id ? { ...lead, status } : lead
        )
      );

      // also update modal if that lead is open
      setSelectedLead((prev) =>
        prev && prev.id === id ? { ...prev, status } : prev
      );
    }
    setUpdating(null);
  };

  const openLeadModal = (lead: LeadRow) => {
    // clone lead into modal state
    setSelectedLead({ ...lead });
  };

  const closeLeadModal = () => {
    setSelectedLead(null);
    setSavingLead(false);
  };

  const handleModalFieldChange = (
    field: keyof LeadRow,
    value: string
  ) => {
    if (!selectedLead) return;
    setSelectedLead({
      ...selectedLead,
      [field]: value,
    });
  };

  const handleSaveLead = async () => {
    if (!selectedLead) return;
    setSavingLead(true);

    try {
      const { error } = await supabase
        .from("leads")
        .update({
          name: selectedLead.name,
          email: selectedLead.email,
          phone: selectedLead.phone,
          message: selectedLead.message,
          status: selectedLead.status,
          source_page: selectedLead.source_page,
        })
        .eq("id", selectedLead.id);

      if (error) {
        console.error("Lead update error:", error);
        alert("Error saving lead: " + error.message);
        setSavingLead(false);
        return;
      }

      // sync list
      setLeads((prev) =>
        prev.map((lead) =>
          lead.id === selectedLead.id ? { ...selectedLead } : lead
        )
      );

      closeLeadModal();
    } catch (err: any) {
      console.error("Unexpected error saving lead:", err);
      alert("Unexpected error: " + err.message);
      setSavingLead(false);
    }
  };

  return (
    <>
    <Helmet>
  <meta name="robots" content="noindex, nofollow" />
</Helmet>

      <Header />
      <main className="min-h-screen px-4 py-8 max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between pb-4 border-b">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Leads Dashboard
            </h1>
            <p className="text-muted-foreground mt-1">
              View and manage leads submitted from your website forms.
            </p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 border border-red-200 rounded-lg p-3 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-16 bg-gray-100 rounded-lg animate-pulse"
              />
            ))}
          </div>
        ) : leads.length === 0 ? (
          <div className="border-2 border-dashed rounded-xl p-6 text-center text-muted-foreground">
            No leads yet.
          </div>
        ) : (
          <div className="border rounded-xl overflow-hidden bg-card shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">
                      Name
                    </th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">
                      Contact
                    </th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">
                      Message
                    </th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground hidden md:table-cell">
                      Source
                    </th>
                    <th className="text-left px-3 py-2 font-medium text-muted-foreground">
                      Status
                    </th>
                    <th className="text-right px-3 py-2 font-medium text-muted-foreground">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {leads.map((lead) => (
                    <tr
                      key={lead.id}
                      className="hover:bg-muted/50 transition-colors"
                    >
                      <td className="px-3 py-2">
                        <div className="font-medium">
                          {lead.name || "—"}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          {new Date(lead.created_at).toLocaleString()}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div>{lead.email || "—"}</div>
                        <div>{lead.phone || ""}</div>
                      </td>
                      <td className="px-3 py-2 text-xs max-w-[260px]">
                        <div className="line-clamp-3">
                          {lead.message || "—"}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground hidden md:table-cell">
                        {lead.source_page || "—"}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <span className="inline-flex px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wide bg-muted">
                          {lead.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex gap-2">
                          {/* quick status buttons */}
                          <Button
                            size="xs"
                            variant="outline"
                            disabled={updating === lead.id}
                            onClick={() => updateStatus(lead.id, "new")}
                          >
                            New
                          </Button>
                          <Button
                            size="xs"
                            variant="outline"
                            disabled={updating === lead.id}
                            onClick={() => updateStatus(lead.id, "contacted")}
                          >
                            Contacted
                          </Button>
                          <Button
                            size="xs"
                            variant="outline"
                            disabled={updating === lead.id}
                            onClick={() => updateStatus(lead.id, "closed")}
                          >
                            Closed
                          </Button>
                          {/* View full form */}
                          <Button
                            size="xs"
                            variant="outline"
                            onClick={() => openLeadModal(lead)}
                          >
                            View
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Modal: Lead details form */}
        {selectedLead && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4">
            <div className="w-full max-w-lg bg-card border rounded-2xl shadow-lg p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold">
                    Lead Details
                  </h2>
                  <p className="text-xs text-muted-foreground mt-1">
                    Created:{" "}
                    {new Date(selectedLead.created_at).toLocaleString()}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closeLeadModal}
                >
                  Close
                </Button>
              </div>

              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium mb-1 block">
                      Name
                    </label>
                    <Input
                      value={selectedLead.name || ""}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleModalFieldChange("name", e.target.value)
                      }
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium mb-1 block">
                      Email
                    </label>
                    <Input
                      value={selectedLead.email || ""}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleModalFieldChange("email", e.target.value)
                      }
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium mb-1 block">
                      Phone
                    </label>
                    <Input
                      value={selectedLead.phone || ""}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleModalFieldChange("phone", e.target.value)
                      }
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium mb-1 block">
                      Source Page
                    </label>
                    <Input
                      value={selectedLead.source_page || ""}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleModalFieldChange("source_page", e.target.value)
                      }
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium mb-1 block">
                    Message
                  </label>
                  <textarea
                    className="w-full min-h-[100px] text-sm border rounded-md p-2"
                    value={selectedLead.message || ""}
                    onChange={(e) =>
                      handleModalFieldChange("message", e.target.value)
                    }
                  />
                </div>

                <div>
                  <label className="text-xs font-medium mb-1 block">
                    Status
                  </label>
                  <select
                    className="w-full border rounded-md px-2 py-1 text-sm"
                    value={selectedLead.status}
                    onChange={(e) =>
                      handleModalFieldChange("status", e.target.value)
                    }
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  type="button"
                  onClick={closeLeadModal}
                  disabled={savingLead}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleSaveLead}
                  disabled={savingLead}
                  className="min-w-[120px]"
                >
                  {savingLead ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}
